/* ==========================================================================
   Rook UI Core — Alert Dialog Controller
   Web Component: <rk-alert-dialog-element>
   Hydration tier: 'interaction' (external trigger pattern)
   ========================================================================== */

import { RkComponent } from '@rook/component';

function waitForExitAnimation(el, done) {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!el || reduce) {
        done();
        return () => {};
    }

    let settled = false;
    const onEnd = (e) => {
        if (e.target !== el) return;
        finish();
    };
    const finish = () => {
        if (settled) return;
        settled = true;
        el.removeEventListener('transitionend', onEnd);
        clearTimeout(timer);
        done();
    };
    const cancel = () => {
        if (settled) return;
        settled = true;
        el.removeEventListener('transitionend', onEnd);
        clearTimeout(timer);
    };
    el.addEventListener('transitionend', onEnd);
    const timer = setTimeout(finish, 500);
    return cancel;
}

class RkAlertDialogElement extends RkComponent {
    static hydration = 'interaction';

    #cancelExitAnim = null;
    #keydownController = null;
    #triggerCleanups = [];

    attachHydrationTrigger(hydrate) {
        const dialog = this.querySelector('.rk-alert-dialog');
        const id = this.dataset.alertDialogId || dialog?.id;
        if (!id) return () => {};

        const triggers = document.querySelectorAll(`[data-alert-dialog-open="${id}"]`);
        const cleanups = [];

        triggers.forEach((trigger) => {
            const onClick = (event) => {
                event.preventDefault();
                trigger.removeEventListener('click', onClick);
                hydrate();
                this.open();
            };
            trigger.addEventListener('click', onClick);
            cleanups.push(() => trigger.removeEventListener('click', onClick));
        });

        return () => cleanups.forEach((fn) => fn());
    }

    setup() {
        this.dialog = this.querySelector('.rk-alert-dialog');
        this.content = this.querySelector('.rk-alert-dialog__content');
        this.previousFocus = null;

        if (!this.dialog) return;

        this.delegate({
            'click [data-action="cancel"]': () => this.close('cancel'),
            'click [data-action="confirm"]': () => {
                this.rkEmit('alert-dialog:confirm', { id: this.dialog.id });
            },
        });

        this.#setupPersistentOpenTriggers();
    }

    teardown() {
        if (this.#cancelExitAnim) {
            this.#cancelExitAnim();
            this.#cancelExitAnim = null;
        }
        if (this.#keydownController) {
            this.#keydownController.abort();
            this.#keydownController = null;
        }
        this.#triggerCleanups.forEach((fn) => fn());
        this.#triggerCleanups = [];
    }

    #setupPersistentOpenTriggers() {
        const dialogId = this.dataset.alertDialogId || this.dialog?.id;
        if (!dialogId) return;

        const triggers = document.querySelectorAll(`[data-alert-dialog-open="${dialogId}"]`);
        triggers.forEach((trigger) => {
            const onClick = (e) => {
                e.preventDefault();
                this.open();
            };
            trigger.addEventListener('click', onClick);
            this.#triggerCleanups.push(() => trigger.removeEventListener('click', onClick));
        });
    }

    isOpen() {
        return this.dialog?.classList.contains('rk-alert-dialog--active');
    }

    open() {
        if (!this.dialog) return;

        if (this.#cancelExitAnim) {
            this.#cancelExitAnim();
            this.#cancelExitAnim = null;
        }

        if (this.isOpen() && this.dialog.dataset.state !== 'closed') return;

        this.previousFocus = document.activeElement;
        this.dialog.classList.add('rk-alert-dialog--active');
        this.dialog.dataset.state = 'open';
        document.body.style.overflow = 'hidden';

        this.#keydownController?.abort();
        this.#keydownController = new AbortController();
        document.addEventListener('keydown', (e) => this.#onKeyDown(e), { signal: this.#keydownController.signal });

        requestAnimationFrame(() => {
            const cancelBtn = this.dialog.querySelector('[data-action="cancel"]');
            if (cancelBtn) {
                cancelBtn.focus();
            } else {
                const first = this.dialog.querySelector(
                    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                );
                if (first) first.focus();
            }
        });

        this.rkEmit('alert-dialog:open', { id: this.dialog.id });
    }

    close(reason) {
        if (!this.dialog || !this.isOpen()) return;
        if (this.dialog.dataset.state === 'closed') return;

        const closeReason = reason || 'cancel';
        this.dialog.dataset.state = 'closed';

        const finalize = () => {
            this.#cancelExitAnim = null;
            this.dialog.classList.remove('rk-alert-dialog--active');
            delete this.dialog.dataset.state;
            document.body.style.overflow = '';

            this.#keydownController?.abort();
            this.#keydownController = null;

            if (this.previousFocus) {
                this.previousFocus.focus();
                this.previousFocus = null;
            }

            this.rkEmit('alert-dialog:close', { id: this.dialog.id, reason: closeReason });
        };

        this.#cancelExitAnim = waitForExitAnimation(this.content, finalize);
    }

    #onKeyDown(e) {
        if (e.key === 'Escape' && this.isOpen()) {
            const escClose = this.dataset.escClose !== 'false';
            if (escClose) {
                e.preventDefault();
                this.close('cancel');
            }
        }
    }
}

if (!customElements.get('rk-alert-dialog-element')) {
    customElements.define('rk-alert-dialog-element', RkAlertDialogElement);
}
